<template>
  <div class="tutorial-intro">
    <div class="grid md:grid-cols-2 gap-6">
      <!-- Key Features -->
      <div class="space-y-4">
        <h4 class="font-semibold text-gray-900 mb-3">🚀 Key Features</h4>

        <div class="flex items-start gap-3 p-3 bg-indigo-50 rounded-lg">
          <IconifyIcon icon="heroicons:shield-check-20-solid" class="w-6 h-6 text-indigo-600 mt-0.5" />
          <div>
            <h5 class="font-medium text-indigo-900">Secure P2P Connection</h5>
            <p class="text-sm text-indigo-700">End-to-end encrypted with no server in between</p>
          </div>
        </div>

        <div class="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
          <IconifyIcon icon="heroicons:qrcode-20-solid" class="w-6 h-6 text-green-600 mt-0.5" />
          <div>
            <h5 class="font-medium text-green-900">QR Code Signaling</h5>
            <p class="text-sm text-green-700">Simple scan-to-connect technology</p>
          </div>
        </div>

        <div class="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
          <IconifyIcon icon="heroicons:bolt-20-solid" class="w-6 h-6 text-purple-600 mt-0.5" />
          <div>
            <h5 class="font-medium text-purple-900">Real-time Communication</h5>
            <p class="text-sm text-purple-700">Instant data transfer with ultra-low latency</p>
          </div>
        </div>
      </div>

      <!-- What You'll Learn -->
      <div class="space-y-4">
        <h4 class="font-semibold text-gray-900 mb-3">📚 What You'll Learn</h4>

        <div class="space-y-3">
          <div class="flex items-center gap-2">
            <IconifyIcon icon="heroicons:check-20-solid" class="w-5 h-5 text-green-500" />
            <span class="text-gray-700">How to create a host connection</span>
          </div>
          <div class="flex items-center gap-2">
            <IconifyIcon icon="heroicons:check-20-solid" class="w-5 h-5 text-green-500" />
            <span class="text-gray-700">Generating and sharing QR codes</span>
          </div>
          <div class="flex items-center gap-2">
            <IconifyIcon icon="heroicons:check-20-solid" class="w-5 h-5 text-green-500" />
            <span class="text-gray-700">Scanning QR codes with your camera</span>
          </div>
          <div class="flex items-center gap-2">
            <IconifyIcon icon="heroicons:check-20-solid" class="w-5 h-5 text-green-500" />
            <span class="text-gray-700">Establishing secure P2P connections</span>
          </div>
          <div class="flex items-center gap-2">
            <IconifyIcon icon="heroicons:check-20-solid" class="w-5 h-5 text-green-500" />
            <span class="text-gray-700">Using chat, file transfer, and video features</span>
          </div>
        </div>

        <!-- Interactive Demo Preview -->
        <div class="mt-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
          <div class="flex items-center gap-2 mb-2">
            <IconifyIcon icon="heroicons:play-20-solid" class="w-5 h-5 text-indigo-600" />
            <span class="font-medium text-indigo-900">Ready to start?</span>
          </div>
          <p class="text-sm text-indigo-700">
            This interactive tutorial will guide you through each step with live demonstrations.
          </p>
        </div>
      </div>
    </div>

    <!-- Security Note -->
    <div class="mt-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
      <div class="flex">
        <div class="flex-shrink-0">
          <IconifyIcon icon="heroicons:shield-check-20-solid" class="w-5 h-5 text-yellow-400" />
        </div>
        <div class="ml-3">
          <h5 class="text-sm font-medium text-yellow-800">Privacy First</h5>
          <div class="mt-1 text-sm text-yellow-700">
            <p>All connections are peer-to-peer. Your data never passes through our servers - it goes directly between devices.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import IconifyIcon from '../IconifyIcon.vue'

// No specific logic needed for intro step
</script>

<style scoped>
.tutorial-intro {
  /* Component-specific styles */
}
</style>